import { LiveWaveform, TaskItemCard, TaskNotice, WaveformPlayer } from '@/features/contribution-kit';
import { cn } from '@/lib/utils';
import { fill, qualityIssueLabel } from '../copy';
import type { InitialDataCopy } from '../initial-data-copy';
import { formatDuration } from '../initial-data.helpers';
import type { AudioQualityState } from '../hooks/use-audio-quality';

type AudioAnswerPanelProps = {
  copy: InitialDataCopy;
  languageName: string;
  isRecording: boolean;
  isPaused: boolean;
  /** Live microphone stream while recording, for the wave. */
  stream: MediaStream | null;
  elapsedMs: number;
  recordedUrl: string | null;
  recordedDurationMs: number;
  micError: boolean;
  /** Spec 006 (D5): the measurement of the finished recording. */
  quality: AudioQualityState;
};

/**
 * Where the spoken answer stands: what to do, a wave that follows the voice
 * with the recorded time, or the take to listen back to with its quality
 * check. Record, stop, pause and redo live in the bottom bar.
 */
export function AudioAnswerPanel(props: AudioAnswerPanelProps) {
  const { copy, isRecording, isPaused, recordedUrl, quality } = props;
  const hasRecording = Boolean(recordedUrl) && !isRecording;

  return (
    <TaskItemCard label={copy.answerLabel}>
      <p className="text-base font-medium text-foreground">{fill(copy.answerIn, { language: props.languageName })}</p>

      {isRecording ? (
        <div
          className={cn(
            'space-y-2 rounded-lg border px-4 py-3',
            isPaused ? 'border-border/60 bg-surface-2' : 'border-danger/30 bg-danger/[0.06]',
          )}
        >
          <LiveWaveform stream={props.stream} paused={isPaused} />
          <div className="flex items-center justify-center gap-3 text-sm" aria-live="polite">
            <span className={cn('size-2 rounded-full', isPaused ? 'bg-muted-foreground' : 'animate-pulse bg-danger')} />
            <span data-testid="initial-data-elapsed" className={cn('font-semibold tabular-nums', isPaused ? 'text-foreground' : 'text-danger')}>
              {formatDuration(props.elapsedMs)}
            </span>
            <span className="text-xs text-muted-foreground">{isPaused ? copy.recordingPaused : copy.recordingLive}</span>
          </div>
        </div>
      ) : hasRecording && recordedUrl ? (
        <div className="space-y-2 rounded-lg border border-border/60 bg-surface-2 p-3">
          <div data-testid="initial-data-playback">
            <WaveformPlayer key={recordedUrl} src={recordedUrl} />
          </div>
          <p className="text-xs font-medium text-success">
            {copy.recorded} · {formatDuration(props.recordedDurationMs)}
          </p>
        </div>
      ) : (
        <p className="rounded-lg bg-surface-2 px-4 py-3 text-center text-sm text-muted-foreground">{copy.audioHint}</p>
      )}

      {hasRecording && quality.status === 'measuring' && (
        <p role="status" className="text-xs text-muted-foreground">{copy.qualityMeasuring}</p>
      )}
      {hasRecording && quality.status === 'ready' && quality.issues.length === 0 && (
        <p className="text-xs font-medium text-success" data-testid="initial-data-quality-ok">{copy.qualityOk}</p>
      )}
      {hasRecording && quality.status === 'ready' && quality.issues.length > 0 && (
        <div role="alert" data-testid="initial-data-quality-issues">
          <TaskNotice tone="warning" title={copy.qualityIssuesTitle}>
            <ul className="list-disc space-y-0.5 pl-5">
              {quality.issues.map((issue) => <li key={issue}>{qualityIssueLabel(copy, issue)}</li>)}
            </ul>
            <p className="mt-1 text-xs">{copy.qualityRedoHint}</p>
          </TaskNotice>
        </div>
      )}

      {props.micError && (
        <div role="alert">
          <TaskNotice tone="danger" icon="mic" title={copy.micError} />
        </div>
      )}
    </TaskItemCard>
  );
}
